/**
 * O painel das sobreposições das verticais (onda 316; pedido do Humberto de 16/10/2026): qual gráfico vai sobre a
 * seção em cada vertical — o perfil medido de qc, qt, fs, u₂, Rf, Ic, Qtn ou Su, ou a coluna das zonas SBT/SBTn nas
 * cores do CPeT-IT —, quais verticais (uma caixa por vertical, «Ligar todas» e «Desligar todas»), a opacidade (0 a
 * 100 %) e a escala (a mesma em todas, para comparar, ou a de cada vertical). O campo estimado embaixo não muda.
 *
 * Só controla: quem desenha é a seção (`SecaoEspacial`) e o 3D (`Modelo3d`, as fitas da Tarefa 316.3) — os dois com
 * o MESMO painel, a mesma conta pura (`sobreposicoesDasVerticais`) e o mesmo estado guardado pela tela (trocar de
 * vista mantém as escolhas). Tudo por propriedade, nada de rota: o Portal usa igual
 * (`mostrarNaoHomologadas`). Acessível: cada controle com rótulo, as caixas e o deslizador pelo teclado.
 */
import type { JSX } from 'react';
import { useId } from 'react';
import { ControleDaEscala } from './ControleDaEscala';
import type { EscalaDoGrafico } from './escalaDoGrafico';
import { GRANDEZAS_DA_SOBREPOSICAO } from './espacialDaCampanha';
import type { GrandezaDaSobreposicao } from './espacialDaCampanha';
import type { MarcaDeHomologacao } from './homologacao';

/** O estado das sobreposições, como a tela o guarda. */
export interface EstadoDasSobreposicoes {
  /** A grandeza sobreposta; null = nenhum gráfico sobre a seção. */
  grandeza: GrandezaDaSobreposicao | null;
  /** As verticais DESLIGADAS (as novas entram ligadas). */
  desligadas: readonly string[];
  /** 0 a 1. */
  opacidade: number;
  escala: 'comum' | 'propria';
}

export function ControleDasSobreposicoes(p: {
  estado: EstadoDasSobreposicoes;
  aoMudar(e: EstadoDasSobreposicoes): void;
  /** As verticais que podem ter a faixa (as estações da linha), na ordem delas. */
  verticais: readonly string[];
  /** A marca de cada grandeza nas verticais (a tela a dá); a não homologada vai marcada, e some no Portal. */
  marcas?: Partial<Record<GrandezaDaSobreposicao, MarcaDeHomologacao>>;
  mostrarNaoHomologadas?: boolean;
  /** O título do painel: «Gráficos das verticais sobre a seção» de partida; o 3D diz «no modelo 3D». */
  titulo?: string;
  /**
   * A escala horizontal escolhida, por grandeza (onda 347, R12): com `aoMudarEscala`, o botão «Escala» do gráfico
   * escolhido (na série); os limites fixos valem para todas as verticais, mesmo com «a de cada vertical».
   */
  escalas?: Readonly<Record<string, EscalaDoGrafico>>;
  aoMudarEscala?(grandeza: string, e: EscalaDoGrafico | null): void;
  /** O domínio desenhado das faixas (o menor início e o maior fim): ao passar a «Fixo», os campos começam nele. */
  dominio?: readonly [number, number] | null;
}): JSX.Element {
  const id = useId();
  const { estado: e } = p;
  const mostrarNao = p.mostrarNaoHomologadas ?? true;
  const opcoes = GRANDEZAS_DA_SOBREPOSICAO.filter((g) => {
    const m = p.marcas?.[g.chave];
    return !m || (m.mostrar && (mostrarNao || m.homologada));
  });
  const mudar = (x: Partial<EstadoDasSobreposicoes>) => p.aoMudar({ ...e, ...x });
  const desligadas = new Set(e.desligadas);
  const ligadas = p.verticais.filter((c) => !desligadas.has(c)).length;
  const def = e.grandeza ? GRANDEZAS_DA_SOBREPOSICAO.find((g) => g.chave === e.grandeza) ?? null : null;
  const marca = e.grandeza ? p.marcas?.[e.grandeza] : undefined;
  const porcento = Math.round(e.opacidade * 100);

  return (
    <fieldset className="cptu-sobreposicoes" aria-describedby={`${id}-nota`}>
      <legend>{p.titulo ?? 'Gráficos das verticais sobre a seção'}</legend>
      <div className="cptu-sobreposicoes-linha">
        <label className="cptu-sobreposicoes-campo">
          <span>Gráfico</span>
          <select value={e.grandeza ?? ''} onChange={(ev) => mudar({ grandeza: (ev.target.value || null) as GrandezaDaSobreposicao | null })}>
            <option value="">Nenhum</option>
            {opcoes.map((g) => {
              const m = p.marcas?.[g.chave];
              return (
                <option key={g.chave} value={g.chave}>
                  {g.rotulo}{g.unidade ? ` (${g.unidade})` : ''}{m && !m.homologada ? ' ▲ não homologada' : ''}
                </option>
              );
            })}
          </select>
        </label>
        <label className="cptu-sobreposicoes-campo">
          <span>Opacidade: {porcento} %</span>
          <input type="range" min={0} max={100} step={5} value={porcento} disabled={!e.grandeza}
            aria-valuetext={`${porcento} %`} onChange={(ev) => mudar({ opacidade: Number(ev.target.value) / 100 })} />
        </label>
        {def?.tipo === 'serie' && p.aoMudarEscala && (
          <div className="cptu-sobreposicoes-campo">
            <ControleDaEscala grandeza={def.chave} rotulo={def.rotulo} unidade={def.unidade} escala={p.escalas?.[def.chave] ?? null}
              dominio={p.dominio ?? undefined} aoMudar={(x) => p.aoMudarEscala?.(def.chave, x)} />
          </div>
        )}
        {def?.tipo === 'serie' && !def.dominio && (
          <div className="cptu-sobreposicoes-campo" role="radiogroup" aria-label="Escala das faixas">
            <span>Escala</span>
            <label className="cptu-sobreposicoes-opcao">
              <input type="radio" name={`${id}-escala`} checked={e.escala === 'comum'} onChange={() => mudar({ escala: 'comum' })} />
              {' '}a mesma em todas
            </label>
            <label className="cptu-sobreposicoes-opcao">
              <input type="radio" name={`${id}-escala`} checked={e.escala === 'propria'} onChange={() => mudar({ escala: 'propria' })} />
              {' '}a de cada vertical
            </label>
          </div>
        )}
      </div>
      {e.grandeza && (
        <div className="cptu-sobreposicoes-linha" role="group" aria-label="Verticais com o gráfico">
          {p.verticais.map((c) => (
            <label key={c} className="cptu-sobreposicoes-opcao">
              <input type="checkbox" checked={!desligadas.has(c)} data-codigo={c}
                onChange={(ev) => {
                  const novo = new Set(desligadas);
                  if (ev.target.checked) novo.delete(c); else novo.add(c);
                  mudar({ desligadas: [...novo] });
                }} />
              {' '}{c}
            </label>
          ))}
          <button type="button" className="cptu-sobreposicoes-botao" disabled={ligadas === p.verticais.length}
            onClick={() => mudar({ desligadas: e.desligadas.filter((c) => !p.verticais.includes(c)) })}>
            Ligar todas
          </button>
          <button type="button" className="cptu-sobreposicoes-botao" disabled={ligadas === 0}
            onClick={() => mudar({ desligadas: [...new Set([...e.desligadas, ...p.verticais])] })}>
            Desligar todas
          </button>
        </div>
      )}
      <p id={`${id}-nota`} className="cptu-nota">
        {e.grandeza
          ? `A faixa de cada vertical é o ${def?.tipo === 'zona' ? 'que o motor classificou em cada leitura' : 'valor medido em cada leitura'} — a vertical como ela é; entre as verticais, o campo estimado.`
          : 'Escolha um gráfico para ver, em cada vertical da linha, o que ela mediu.'}
        {marca && !marca.homologada && mostrarNao ? ` ▲ não homologada${marca.motivo ? ` — ${marca.motivo}` : ''}.` : ''}
      </p>
    </fieldset>
  );
}
